/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  Input,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getEstadosGlobales,
  getEstados,
  getMunicipios,
} from 'api/catalogos/index';
import {
  getRegistroPatronal,
  getRegistrosPatronales,
  postRegistroPatronal,
  deleteRegistroPatronal,
  patchRegistroPatronal,
} from 'api/catalogos/registrosPatronales';
import {
  getTiposDeSeguridadesSociales,
} from 'api/catalogos/tiposDeSeguridadesSociales';
import { checkToken } from 'api/utils';
import {
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

export const tabRegistroPatronalPermission = {
  permissionModel: 'registropatronal',
  catalogsModels: [
    'tipodeseguridadsocial',
    'estado',
    'municipio',
  ],
  tabsModels: [],
};

const RegistroPatronal = ({ permission }) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estadosGlobales, setEstadosGlobales] = useState([]);
  const [estado, setEstado] = useState();
  const [municipio, setMunicipio] = useState();
  const [tipoDeSeguridadSocial, setTipoDeSeguridadSocial] = useState();
  const [modalMunicipio, setModalMunicipio] = useState([]);
  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getRegistrosPatronales();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estadosGlobales,
        _estado,
        _municipio,
        _tipoDeSeguridadSocial,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getEstados(),
        getMunicipios(),
        getTiposDeSeguridadesSociales({ estados_globales: ESTATUS_AUTORIZADO }),
        getRegistrosPatronales(),
      ]);
      if (mounted) {
        setEstadosGlobales(_estadosGlobales);
        setEstado(_estado);
        setMunicipio(_municipio);
        setTipoDeSeguridadSocial(_tipoDeSeguridadSocial);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    form.resetFields();
    setVisible(false);
    setModalMunicipio();
    formDatos.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _registro_patronal = await getRegistroPatronal(match.id);
    const _municipios = await getMunicipios({ estado: _registro_patronal?.estado });
    setModalMunicipio(_municipios);
    setTimeout(() => {
      if (_registro_patronal) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_registro_patronal));
          formDatos.setFieldsValue(formatReceived(_registro_patronal));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchRegistroPatronal(key, values) : postRegistroPatronal(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteRegistroPatronal(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };
  const columns = [
    {
      titleText: 'Tipo de Seguridad Social',
      dataIndex: 'tipo_de_seguridad_social',
      key: 'tipo_de_seguridad_social',
      width: 200,
      render: (val) => tipoDeSeguridadSocial.find((e) => e.id === val)?.concepto,
    },
    {
      titleText: 'No. Registro Patronal',
      dataIndex: 'numero_registro_patronal',
      key: 'numero_registro_patronal',
      width: 200,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 200,
      render: (val) => estado.find((e) => e.id === val)?.nombre_de_AGEE,
    },
    {
      titleText: 'Municipio',
      dataIndex: 'municipio',
      key: 'municipio',
      width: 200,
      render: (val) => municipio.find((e) => e.id === val)?.nom_mun,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];
  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    numero: [
      requiredRule,
      {
        pattern: /^\d{11}$/,
        message: 'Ingrese un No. de Registro Patronal válido',
      },
    ],
  };

  const changeState = async (estadoParam) => {
    setLoading(true);
    const formatParam = { estado: estadoParam };
    form.setFieldsValue({
      estadoParam,
    });
    const _municipios = await getMunicipios(formatParam);
    setModalMunicipio(_municipios);
    setLoading(false);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
        />

        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Registro Patronal`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_seguridad_social"
                label="Tipo de Seguridad Social"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={tipoDeSeguridadSocial}
                  labelProp="concepto"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="numero_registro_patronal"
                rules={rules.numero}
                label="No. de Registro Patronal"
                hasFeedback
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estado"
                label="Estado"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={estado}
                  labelProp="nombre_de_AGEE"
                  onChange={(value) => {
                    if (value >= 0) {
                      form.resetFields(['municipio']);
                      changeState(value);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="municipio"
                label="Municipio"
                rules={rules.required}
                hasFeedback
              >
                <Select dataSource={modalMunicipio} labelProp="nom_mun" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estadosGlobales}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Registro Patronal ${form.getFieldValue('numero_registro_patronal')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default RegistroPatronal;
